import { useTranslation } from "react-i18next";
import LinkedinPreview from "./LinkedinPreview";
import UserAvatar from "../../../shared/UserAvatar";
import PlatformIcons, { platformsOfSchedule, PLATFORM_ICON } from "../../../shared/PlatformIcons";
import { ApprovalBadge } from "../Approvals/approvalStyles";
import { formatDate } from "@serie3/common/Shared/timeingStuff";

const nameOf = (p) => (p?.name && String(p.name).trim()) || "- -";
/** Initials source: name, else the e-mail; the visible label never shows the e-mail. */
const initialsSource = (p) => (p?.name && String(p.name).trim()) || p?.username || "?";

const STATUS_STYLES = {
  draft:       { bg: "rgba(107,114,128,0.12)", color: "#4b5563" },
  in_progress: { bg: "rgba(3,142,220,0.10)",   color: "#038edc" },
  ready:       { bg: "rgba(86,74,177,0.12)",   color: "#564ab1" },
  scheduled:   { bg: "rgba(241,115,79,0.12)",  color: "#c2410c" },
  published:   { bg: "rgba(81,210,140,0.15)",  color: "#1a7a4a" },
  error:       { bg: "rgba(243,78,78,0.12)",   color: "#c0392b" },
};

const Label = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#9299a8", marginBottom: 6 }}>
    {children}
  </div>
);

const Person = ({ person, size = 26 }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
    <UserAvatar userId={person?.id} name={initialsSource(person)} size={size} />
    <span style={{ fontSize: 13, fontWeight: 600, color: "#1a2236", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {nameOf(person)}
    </span>
  </span>
);

/**
 * Two-column post preview: the rendered content on the left, the facts on the
 * right (author, status, who it is planned for, platforms and dates).
 *
 * `author`    {id, name} of the post's creator
 * `schedules` the post's schedules (defaults to post.schedules)
 * `files`     attached images for the preview
 * `binaryEndpoint` where image bytes come from; group views pass the
 *           member-gated group endpoint, personal views use the default
 */
const PostPreviewPanel = ({ post, author = null, schedules = null, files = [], binaryEndpoint = undefined, children = null }) => {
  const { t } = useTranslation();
  const list = schedules ?? post?.schedules ?? [];
  const delegated = list.filter((s) => s?.publish_as);
  const platforms = [...new Set(list.flatMap((s) => platformsOfSchedule(s)))];
  const status = post?.status || (post?.draft ? "draft" : null);
  const statusStyle = STATUS_STYLES[status] ?? STATUS_STYLES.draft;

  return (
    <div style={{ display: "flex", gap: 0, flexWrap: "wrap" }}>
      {/* Content takes two thirds; the preview keeps its own card width and is centred in that space. */}
      <div style={{ flex: "2 1 400px", minWidth: 0, padding: "20px 24px", background: "#f5f7fa", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={{ width: "100%", maxWidth: 560 }}>
        <LinkedinPreview
          text={post?.text}
          postFiles={files}
          poll={post?.poll}
          post={post}
          name={author ? nameOf(author) : null}
          {...(binaryEndpoint ? { binaryEndpoint } : {})}
        />
        </div>
      </div>

      <div style={{ flex: "1 1 220px", minWidth: 0, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18, borderLeft: "1px solid #eaecf0" }}>
        <div>
          <Label>{t("Backend.Post.Preview.CreatedBy", "Created by")}</Label>
          <Person person={author} />
        </div>

        <div>
          <Label>{t("Backend.Post.Preview.Status", "Status")}</Label>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, textTransform: "capitalize" }}>
            {status ? t(`Backend.Post.State.${status.charAt(0).toUpperCase() + status.slice(1)}`, status.replace("_", " ")) : "—"}
          </span>
        </div>

        {delegated.length > 0 && (
          <div>
            <Label>{t("Backend.Post.Preview.PlannedFor", "Planned for")}</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {delegated.map((s) => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <Person person={s.publish_as} size={24} />
                  <ApprovalBadge status={s.approval_status} title={s.approval_comment} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label>{t("Backend.Post.Preview.Platforms", "Platforms")}</Label>
          {platforms.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {platforms.map((p) => (
                <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1a2236" }}>
                  <PlatformIcons platforms={[p]} size={15} title="" />
                  {PLATFORM_ICON[p]?.label ?? p}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 13, color: "#9299a8" }}>{t("Backend.Post.Preview.NotScheduled", "Not scheduled yet")}</span>
          )}
        </div>

        {list.length > 0 && (
          <div>
            <Label>{t("Backend.Post.Preview.Dates", "Dates")}</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {list.map((s) => (
                <div key={s.id} style={{ fontSize: 13, color: "#1a2236", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <PlatformIcons platforms={platformsOfSchedule(s)} size={13} title="" />
                  <span>{formatDate(s.local_user_time || s.scheduled_for)}</span>
                  <span style={{ fontSize: 11, color: "#9299a8" }}>{s.timezone}</span>
                  {(s.posted_at || s.post_status === "published") && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#1a7a4a" }}>{t("Backend.Post.State.Published", "Published")}</span>
                  )}
                  {s.post_status === "error" && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#c0392b" }} title={s.post_error || undefined}>{t("Backend.Post.State.MissedSchedule", "Not published")}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

export default PostPreviewPanel;
